import { useState, useEffect } from 'react'
import { X, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import KPITrendChart from './KPITrendChart'

interface KPIData {
  id: number
  name: string
  description: string
  current_value: number
  target_value: number
  unit: string
  trend: string
  change_percentage: number
  period: string
  category: string
}

interface KPIDetailModalProps {
  kpi: KPIData
  isOpen: boolean
  onClose: () => void
}

export default function KPIDetailModal({ kpi, isOpen, onClose }: KPIDetailModalProps) {
  const [historicalData, setHistoricalData] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (isOpen && kpi) {
      fetchHistoricalData()
    }
  }, [isOpen, kpi])

  const fetchHistoricalData = async () => {
    setLoading(true)
    try {
      // For now, generate mock historical data
      // In production, this would fetch from /api/kpis/${kpi.id}/history
      const mockData = generateMockHistoricalData(kpi)
      setHistoricalData(mockData)
    } catch (error) {
      console.error('Error fetching historical data:', error)
    } finally {
      setLoading(false)
    }
  }

  const generateMockHistoricalData = (kpi: KPIData) => {
    const months = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec (Current)']
    const currentValue = kpi.current_value
    const targetValue = kpi.target_value
    
    // Generate realistic trend based on current progress
    const progress = (currentValue / targetValue) * 100
    const monthlyGrowth = progress / 6 // Distribute progress over 6 months
    
    const actualValues = months.map((_, index) => {
      const baseValue = (index + 1) * monthlyGrowth * (targetValue / 100)
      const variance = baseValue * (Math.random() * 0.1 - 0.05) // ±5% random variance
      return Math.max(0, Math.round(baseValue + variance))
    })
    
    // Last value should be close to current value
    actualValues[actualValues.length - 1] = currentValue
    
    const targetValues = months.map(() => targetValue)

    return {
      labels: months,
      datasets: [
        {
          label: 'Actual',
          data: actualValues,
          borderColor: '#2f577b',
          backgroundColor: 'rgba(47, 87, 123, 0.1)',
        },
        {
          label: 'Target',
          data: targetValues,
          borderColor: '#b68a2f',
          backgroundColor: 'rgba(182, 138, 47, 0.1)',
        },
      ],
    }
  }

  if (!isOpen) return null

  const progress = (kpi.current_value / kpi.target_value) * 100
  const gap = kpi.target_value - kpi.current_value
  const isAheadOfTarget = kpi.current_value >= kpi.target_value

  const getTrendIcon = () => {
    if (kpi.trend === 'up') return <TrendingUp className="w-5 h-5 text-green-600" />
    if (kpi.trend === 'down') return <TrendingDown className="w-5 h-5 text-red-600" />
    return <Minus className="w-5 h-5 text-gray-600" />
  }

  const getTrendColor = () => {
    if (kpi.trend === 'up') return 'text-green-600'
    if (kpi.trend === 'down') return 'text-red-600'
    return 'text-gray-600'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-vampire-black">{kpi.name}</h2>
              <span className="text-xs px-3 py-1 rounded-full bg-royal-blue-100 text-royal-blue-800 border border-royal-blue-300 font-semibold">
                {kpi.category}
              </span>
            </div>
            <p className="text-sm text-graphite-grey">{kpi.description}</p>
          </div>
          <button
            onClick={onClose}
            className="text-graphite-grey hover:text-vampire-black transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Current Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Current Value */}
            <div className="bg-pearl-white p-6 rounded-lg">
              <p className="text-sm text-graphite-grey mb-1">Current Value</p>
              <p className="text-3xl font-bold text-royal-blue">
                {kpi.current_value.toLocaleString()} {kpi.unit}
              </p>
            </div>

            {/* Target Value */}
            <div className="bg-pearl-white p-6 rounded-lg">
              <p className="text-sm text-graphite-grey mb-1">Target Value</p>
              <p className="text-3xl font-bold text-golden">
                {kpi.target_value.toLocaleString()} {kpi.unit}
              </p>
            </div>

            {/* Progress */}
            <div className="bg-pearl-white p-6 rounded-lg">
              <p className="text-sm text-graphite-grey mb-1">Progress</p>
              <p className={`text-3xl font-bold ${isAheadOfTarget ? 'text-green-600' : 'text-vampire-black'}`}>
                {Math.round(progress)}%
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-vampire-black">
                Overall Progress
              </span>
              <span className="text-sm text-graphite-grey">
                {isAheadOfTarget ? (
                  <span className="text-green-600 font-semibold">
                    Exceeded by {(kpi.current_value - kpi.target_value).toLocaleString()} {kpi.unit}
                  </span>
                ) : (
                  <span>
                    Gap: {gap.toLocaleString()} {kpi.unit}
                  </span>
                )}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className={`h-4 rounded-full transition-all ${
                  progress >= 100 ? 'bg-green-500' :
                  progress >= 70 ? 'bg-blue-500' :
                  progress >= 50 ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          </div>

          {/* Trend Indicator */}
          <div className="bg-pearl-white p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getTrendIcon()}
                <div>
                  <p className="text-sm font-semibold text-vampire-black">Trend</p>
                  <p className="text-xs text-graphite-grey">{kpi.period}</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-2xl font-bold ${getTrendColor()}`}>
                  {kpi.change_percentage > 0 ? '+' : ''}{kpi.change_percentage}%
                </p>
                <p className="text-xs text-graphite-grey">vs previous period</p>
              </div>
            </div>
          </div>

          {/* Historical Trend Chart */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-vampire-black mb-4">
              6-Month Trend
            </h3>
            {loading ? (
              <div className="flex items-center justify-center h-[300px]">
                <p className="text-graphite-grey">Loading chart data...</p>
              </div>
            ) : historicalData ? (
              <KPITrendChart
                data={historicalData}
                title={`${kpi.name} Trend`}
                unit={kpi.unit}
              />
            ) : (
              <div className="flex items-center justify-center h-[300px]">
                <p className="text-graphite-grey">No historical data available</p>
              </div>
            )}
          </div>

          {/* Insights */}
          <div className="bg-royal-blue-50 border border-royal-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-royal-blue-900 mb-2">Insights</h4>
            <ul className="space-y-1 text-sm text-royal-blue-800">
              {progress >= 100 && (
                <li>• Target exceeded! Excellent performance.</li>
              )}
              {progress >= 70 && progress < 100 && (
                <li>• On track to meet target with current trajectory.</li>
              )}
              {progress < 70 && progress >= 50 && (
                <li>• Moderate progress. Consider acceleration strategies.</li>
              )}
              {progress < 50 && (
                <li>• Below expected progress. Immediate action recommended.</li>
              )}
              {kpi.trend === 'up' && (
                <li>• Positive trend indicates improving performance.</li>
              )}
              {kpi.trend === 'down' && (
                <li>• Downward trend requires attention and intervention.</li>
              )}
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-royal-blue text-white rounded-lg hover:bg-royal-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
